export type DifficultyLevel = 'again' | 'hard' | 'good' | 'easy' | 'custom';

export interface SubCard {
    question: string;
    answer: string;
    group?: string;
}

export interface SubcardReviewResult {
    subcardIndex: number;
    subcard: SubCard;
    timeSpent: number;
    isCorrect?: boolean;
    /** Not answered on its own: graded by the whole-card rating ("Finish & rate"). */
    inferred?: boolean;
    difficulty?: 'easy' | 'good' | 'again';
}

/** Where an answer was given: Review, Car mode, or the per-card Practice. */
export type ReviewSource = 'review' | 'car' | 'practice';

export interface MultiCardSessionResult {
    cardId: string;
    totalTime: number;
    subcardResults: SubcardReviewResult[];
    sessionResult: DifficultyLevel;
    completedAt: number;
}

export interface AddCardPayload {
    markup: string;
    images: Record<number, string>;
    hasSubCards?: boolean;
    subCards?: SubCard[];
}

export interface ApplyReviewPayload {
    cardId: string,
    intervalMinutes: number,
    difficulty?: DifficultyLevel,
    source?: Exclude<ReviewSource, 'practice'>,
    isMultiCard?: boolean,
    multiCardResult?: MultiCardSessionResult
}

export interface PracticeSubcardPayload {
    cardId: string;
    subcard: SubCard;
    isCorrect: boolean;
    /** Milliseconds from showing the subcard to grading it. */
    timeSpent?: number;
}

export type TtsVoice = 'native' | 'greek';

export interface TtsPayload {
    text: string;
    voice: TtsVoice;
    /** Picks one of the voice's ids (`variant % ids.length`); a random one when omitted. */
    variant?: number;
    /** Speaks with exactly this voice (previewing a voice in the settings). */
    voiceId?: string;
}

export interface TtsVoiceDto {
    /** ElevenLabs voice id. */
    id: string;
    /** Optional label shown in the voice settings. */
    name?: string;
}

/** A single-use token for ElevenLabs' realtime speech-to-text (Car mode voice control). */
export interface SttTokenDto {
    token: string;
}

/** The voice collection per language; Car mode picks one voice per card from each list. */
export type TtsVoicesDto = Record<TtsVoice, TtsVoiceDto[]>;

export enum CardState {
    Known = 0,
    Future = 1,
    Memorizer = 2,
}

export interface CardDto {
    cardId: string;
    markup: string;
    interval: number;
    lastCheckTime: number,
    nextCheckTime: number,
    passedTime: number,
    images: Record<string, string>;
    state: number,
    hasBackup: boolean,
    /** The card's latest background regenerate/invert, while running or after it failed. */
    generation?: CardGenerationDto,
    /** Set while the card is marked for rework (e.g. regeneration). */
    mark?: CardMarkDto,
}

/** Something on the card is marked for rework: the whole card and/or some of its subcards. */
export interface CardMarkDto {
    /** The card as a whole is marked (otherwise only subcards are). */
    wholeCard: boolean;
    /** What to change on the whole card; may be empty, always empty unless wholeCard. */
    note: string;
    markedTime: number;
    /** Individual subcards marked for rework. */
    subcards: SubcardMarkDto[];
}

export interface SubcardMarkDto {
    /** subcardKey(question, answer) of the subcard when it was marked. */
    subcardKey: string;
    /** The subcard's question at that time, so the mark stays readable after an edit. */
    question: string;
    note: string;
    markedTime: number;
}

export interface UnmarkResult {
    /** The card's mark left after the change; null when nothing is marked any more. */
    mark: CardMarkDto | null;
}

export interface MarkCardPayload {
    note?: string;
}

export interface UnmarkCardsPayload {
    cardIds: string[];
}

export type CardGenerationKind = 'regenerate' | 'invert';

export type CardGenerationStatus = 'running' | 'done' | 'error' | 'dismissed';

export interface CardGenerationDto {
    id: string;
    kind: CardGenerationKind;
    status: CardGenerationStatus;
    error?: string;
    createdTime: number;
}

export interface CardGenerationStateDto {
    generation?: CardGenerationDto;
}


export interface ReviewCard {
    card?: CardDto,
    dailyStat?: {
        reviewedToday: number,
        totalCardsForReview: number,
    },
    error?: string
}

export interface SetMarkupPayload {
    cardId: string;
    markup: string;
}

export interface SetStatePayload {
    cardId: string;
    state: number;
}

export interface SetStateResult {
    updatedCardIds: string[];
}

export type QuickAddStatus = 'pending' | 'processing' | 'done' | 'error' | 'duplicate';

export interface QuickAddCardRef {
    cardId: string;
    markup: string;
}

export interface QuickAddItemDto {
    id: string;
    text: string;
    language: 'greek' | 'english';
    status: QuickAddStatus;
    error?: string;
    warnings?: string[];
    normalCard?: QuickAddCardRef;
    invertedCard?: QuickAddCardRef;
    createdTime: number;
}

export type LlmSessionKind = 'quick-add' | 'regenerate' | 'invert';

export type LlmProvider = 'deepseek' | 'chatgpt';

export const MAX_REGENERATE_TEXT_LENGTH = 200;

export const MAX_REGENERATE_INSTRUCTIONS_LENGTH = 2000;

export const MAX_CARD_MARK_NOTE_LENGTH = 2000;

export type LlmRequestStatus = 'ok' | 'error';

export interface LlmMessageDto {
    role: string;
    content: string;
}

export interface LlmRequestLogDto {
    id: string;
    step: string;
    direction?: string;
    model: string;
    messages: LlmMessageDto[];
    response?: string;
    promptTokens?: number;
    completionTokens?: number;
    totalTokens?: number;
    costUsd?: number;
    durationMs: number;
    status: LlmRequestStatus;
    error?: string;
    createdTime: number;
}

export interface LlmSessionSummaryDto {
    id: string;
    kind: LlmSessionKind;
    sourceText: string;
    language?: string;
    cardIds: string[];
    requestCount: number;
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
    costUsd?: number;
    createdTime: number;
}


export interface LlmSessionDetailDto extends LlmSessionSummaryDto {
    requests: LlmRequestLogDto[];
}
